import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { isMobileOnly } from 'react-device-detect';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField'; // Import MUI TextField
import { BLACK_COLOR, GREEN_COLOR } from 'src/utils/pnd/PndConstants';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InputAdornment from '@mui/material/InputAdornment';

const DateTimePickerField = ({ onChange, value, label, size, ...custom }) => {
  const [startDate, setStartDate] = useState(value ? new Date(value) : null);

  useEffect(() => {
    setStartDate(value ? new Date(value) : null);
  }, [value]);

  const handleChange = (date) => {
    setStartDate(date);
    onChange(date);
  };

  const isSmall = size === 'small';

  return (
    <div className="date-picker" style={{ width: '100%' }}>
      <DatePicker
        timeFormat="HH:mm"
        selected={startDate}
        onChange={handleChange}
        showTimeSelect
        dateFormat="MMMM d, yyyy h:mm aa"
        dropDownMode="select"
        withPortal={isMobileOnly}
        wrapperClassName="w-100" // Ensure react-datepicker wrapper is full width
        customInput={(
          <TextField
            label={!startDate ? label : ""} // Only show label if no value (acting as placeholder), or let InputLabel handle it? MUI TextField handles label.
            // Actually, if we pass label to TextField, it works as floating label.
            // But here we want compact.
            hiddenLabel={isSmall} // Hide label if compact to match others? Or keep it? The other compact fields use a label ABOVE the input.
            // In OrderHistory, we have distinct header labels. So we might NOT want a label inside the field if it duplicates.
            // The user code in OrderHistory has: <label>Order From Date</label> <Field ... />
            // So we should NOT show a label inside the TextField if standard label is used.
            // But previously check code:
            // <TextField label={label} ... />
            // So it WAS showing a label.
            // If `OrderHistory` passes a label prop to Field, it shows inside.
            // In OrderHistory snippet: <label...>Order From Date</label> <Field name="..." component={...} />
            // It does NOT pass `label` prop to Field in the updated code in Step 324!
            // Wait, in Step 324 code:
            // <Field name="orderFromDate" component={renderMuiDateTimePickerField} ... />
            // No label prop passed to Field.
            // So label inside TextField is undefined.

            value={startDate ? startDate.toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true
            }) : ''}
            onClick={(e) => e.preventDefault()}
            variant="outlined"
            fullWidth
            size={size || "medium"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarMonthIcon style={{ fontSize: isSmall ? '1rem' : '1.5rem', color: '#9ca3af' }} />
                </InputAdornment>
              ),
              style: {
                height: isSmall ? '32px' : '56px',
                fontSize: isSmall ? '0.85rem' : '1rem',
                paddingRight: 0,
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingRight: '8px !important',
                '& input': {
                  padding: isSmall ? '0 8px' : '16.5px 14px',
                  height: isSmall ? '32px' : 'auto',
                  fontSize: isSmall ? '0.85rem' : '1rem',
                },
                '& fieldset': {
                  borderColor: '#dee2e6', // Matches bootstrap/custom border
                },
                '&:hover fieldset': {
                  borderColor: BLACK_COLOR,
                },
                '&.Mui-focused fieldset': {
                  borderColor: GREEN_COLOR,
                },
              },
            }}
          />
        )}
      />
    </div>
  );
};

DateTimePickerField.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ]),
  label: PropTypes.string,
  size: PropTypes.string,
};

const renderMuiDateTimePickerField = ({ input, label, meta: { touched, error }, ...custom }) => (
  <div className="w-100">
    <DateTimePickerField {...input} label={label} {...custom} />
    {touched && error && <div className="text-danger small mt-1">{error}</div>}
  </div>
);

renderMuiDateTimePickerField.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func,
    name: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.string,
    ]),
  }).isRequired,
  label: PropTypes.string,
  meta: PropTypes.object,
};

export default renderMuiDateTimePickerField;